import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/shared/lib/supabase';
import { env } from '@/app/env';
import { logger } from '@/shared/lib/logger';
import { toast } from '@/shared/lib/toast';
import i18n from '@/i18n';
import { notificationKeys } from '../api';

/**
 * Sprint M — título del toast in-app según el kind de la notificación nueva.
 * El payload Realtime solo trae la fila cruda (sin actor/grupo, que vienen de
 * joins), así que el texto es un rótulo corto por tipo. Fallback genérico.
 */
const KIND_TOAST_KEY: Record<string, string> = {
  expense_created: 'notifications.toast_expense_created',
  expense_updated: 'notifications.toast_expense_updated',
  expense_commented: 'notifications.toast_expense_commented',
  added_to_group: 'notifications.toast_added_to_group',
  settlement_received: 'notifications.toast_settlement_received',
  recurring_generated: 'notifications.toast_recurring_generated',
  moderation_pending: 'notifications.toast_moderation_pending',
  invitation_received: 'notifications.toast_invitation_received',
};

interface RealtimeNotifPayload {
  eventType?: string;
  new?: { inapp?: boolean; kind?: string } | null;
}

/**
 * Sprint K.0 — notificaciones en tiempo real. Sprint M — toast in-app.
 *
 * Sustituye (de facto) el polling de 60s del badge de la campanita: se
 * suscribe al canal Realtime de `notifications` filtrado por `recipient_id`
 * y, ante cualquier INSERT/UPDATE/DELETE de una notificación mía, invalida
 * las queries de la campanita (`unread-count` + `list`). El badge reacciona
 * al instante en vez de esperar al siguiente refetch.
 *
 * Sprint M: además, al INSERT de una notificación nueva in-app, muestra un
 * toast informativo con un rótulo corto según el kind.
 *
 * El `refetchInterval` de los hooks de query se mantiene como red de
 * seguridad por si el canal WebSocket cae (roaming, sleep del móvil, etc.).
 *
 * No-op si:
 *  - `VITE_ENABLE_REALTIME` está en `false` (kill-switch por entorno).
 *  - Aún no hay `participantId` (usuario no cargado / no logueado).
 *
 * @param participantId  id del participant logueado (== `profile.id`).
 */
export function useNotificationsRealtime(participantId: string | undefined): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!env.VITE_ENABLE_REALTIME) return;
    if (!participantId) return;

    const onEvent = (payload: RealtimeNotifPayload) => {
      // Badge/lista al instante (K.0).
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
      // M: toast in-app SOLO para el INSERT de una notificación nueva que el
      // usuario quiere in-app (inapp=true). UPDATE (marcar leído) y DELETE no
      // deben lanzar toast.
      if (payload.eventType === 'INSERT' && payload.new?.inapp) {
        const key = KIND_TOAST_KEY[payload.new.kind ?? ''] ?? 'notifications.toast_new';
        toast.info(i18n.t(key));
      }
    };

    const channel = supabase
      .channel(`notifications:${participantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${participantId}`,
        },
        onEvent,
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          logger.error('notifications realtime channel error', { code: status });
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [participantId, qc]);
}
